import React, { useEffect, useMemo, useState, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  updateData,
  getData,
  parseCurrency,
  formatCurrency,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  Pack_Id: undefined,
  Pack_Name: "",
  Pack_Price: "",
  Pack_Status: "Y",
  Pack_Img: "",
};

const PackageForm = ({
  callInitialize,
  mode,
  name,
  initialData,
  serviceOptions = [],
  setMode,
}) => {
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState({
    form: DEFAULT_FORM,
    services: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const [modalSelection, setModalSelection] = useState([]);
  const companyId = useMemo(() => window.localStorage.getItem("company"), []);

  const sanitizedInitialData = useMemo(() => {
    if (mode === "U" && initialData) {
      return {
        form: {
          Pack_Id: initialData.Pack_Id,
          Pack_Name: initialData.Pack_Name || "",
          Pack_Img: initialData.Pack_Img || "",
          Pack_Price:
            initialData.Pack_Price !== undefined && initialData.Pack_Price !== null
              ? String(initialData.Pack_Price)
              : "",
          Pack_Status: initialData.Pack_Status === "N" ? "N" : "Y",
        },
        services: Array.isArray(initialData.services)
          ? initialData.services
          : [],
      };
    }
    return {
      form: { ...DEFAULT_FORM },
      services: [],
    };
  }, [initialData, mode]);

  useEffect(() => {
    const nextForm = sanitizedInitialData.form;
    const nextServices = sanitizedInitialData.services;
    setFormValues(nextForm);
    setSelectedServices(nextServices);
    setInitialSnapshot({
      form: nextForm,
      services: nextServices,
    });
  }, [sanitizedInitialData]);

  useEffect(() => {
    setImagePreview(buildImagePreviewSrc(formValues.Pack_Img));
  }, [formValues.Pack_Img]);

  useEffect(() => {
    if (isServiceModalOpen) {
      setModalSelection(selectedServices.map((service) => Number(service.Service_Id)));
      setServiceSearch("");
    }
  }, [isServiceModalOpen, selectedServices]);

  const buildImagePreviewSrc = (value) => {
    if (!value || typeof value !== "string") return null;
    if (value.startsWith("data:image/")) return value;
    const mime = detectImageMime(value);
    return `data:${mime};base64,${value}`;
  };

  const detectImageMime = (value) => {
    const DEFAULT_IMAGE_MIME = "image/png";
    if (!value || typeof value !== "string") return DEFAULT_IMAGE_MIME;
    if (value.startsWith("data:image/")) {
      const match = value.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
      return match ? match[1] : DEFAULT_IMAGE_MIME;
    }
    const signature = value.slice(0, 20);
    if (signature.startsWith("/9j/") || signature.startsWith("/9J/")) return "image/jpeg";
    if (signature.startsWith("iVBOR")) return "image/png";
    if (signature.startsWith("R0lGOD")) return "image/gif";
    if (signature.startsWith("Qk")) return "image/bmp";
    return DEFAULT_IMAGE_MIME;
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          const parts = result.split("base64,");
          resolve(parts.length > 1 ? parts[1] : result);
        } else {
          resolve(null);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      getAlert("FAILED", "รองรับเฉพาะไฟล์ PNG หรือ JPEG เท่านั้น");
      event.target.value = "";
      return;
    }

    const maxFileSize = 5 * 1024 * 1024;
    if (file.size > maxFileSize) {
      getAlert("FAILED", "ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 5MB)");
      event.target.value = "";
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      setFormValues((prev) => ({ ...prev, Pack_Img: base64 }));
      setImagePreview(buildImagePreviewSrc(base64));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error converting image:", error);
      getAlert("FAILED", "ไม่สามารถอ่านไฟล์รูปภาพได้ กรุณาลองใหม่");
    }
  };

  const handleRemoveImage = () => {
    setFormValues((prev) => ({ ...prev, Pack_Img: "" }));
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFileDialog = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleBack = () => {
    if (typeof setMode === "function") {
      setMode("S");
    } else {
      callInitialize();
    }
  };

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handlePackPriceFocus = () => {
    const val = formValues.Pack_Price || "";
    const parsed = parseCurrency(val);
    const raw =
      typeof parsed === "number" && !Number.isNaN(parsed)
        ? String(parsed)
        : String(val).replace(/,/g, "");
    setFormValues((prev) => ({ ...prev, Pack_Price: raw }));
  };

  const handlePackPriceBlur = (event) => {
    const value = event.target.value;
    const parsed = parseCurrency(value);
    const num =
      typeof parsed === "number" && !Number.isNaN(parsed)
        ? Number(parsed)
        : Number.parseFloat(String(value).replace(/,/g, ""));
    if (!Number.isNaN(num)) {
      setFormValues((prev) => ({
        ...prev,
        Pack_Price: formatCurrency(num, false),
      }));
    } else {
      setFormValues((prev) => ({ ...prev, Pack_Price: value }));
    }
  };

  const previewContainerStyle = {
    width: "180px",
    height: "180px",
    backgroundColor: "#f8f9fc",
    border: "1px dashed #d7dce3",
    borderRadius: "12px",
    overflow: "hidden",
  };

  const previewImageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  };

  const uploadButtonStyle = {
    backgroundColor: "rgb(225, 63, 87)",
    borderColor: "rgb(225, 63, 87)",
    color: "#fff",
    padding: "8px 18px",
    borderRadius: "10px",
    fontWeight: 600,
  };

  const removeButtonStyle = {
    borderColor: "rgb(225, 63, 87)",
    color: "rgb(225, 63, 87)",
    padding: "8px 18px",
    borderRadius: "10px",
    fontWeight: 600,
  };

  const handleReset = () => {
    setFormValues(initialSnapshot.form);
    setSelectedServices(initialSnapshot.services);
    setImagePreview(buildImagePreviewSrc(initialSnapshot.form.Pack_Img));
  };

  const handleRemoveService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.filter((service) => Number(service.Service_Id) !== Number(serviceId))
    );
  };

  const toggleModalSelection = (serviceId) => {
    setModalSelection((prev) => {
      const normalizedId = Number(serviceId);
      if (prev.includes(normalizedId)) {
        return prev.filter((id) => id !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  };

  const handleServiceModalConfirm = () => {
    const selected = serviceOptions
      .filter((service) => modalSelection.includes(Number(service.Service_Id)))
      .map((service) => ({
        Service_Id: service.Service_Id,
        Service_Code: service.Service_Code,
        Service_Name: service.Service_Name,
      }))
      .sort((a, b) => (a.Service_Name || "").localeCompare(b.Service_Name || ""));

    setSelectedServices(selected);
    setIsServiceModalOpen(false);
  };

  const validateForm = () => {
    const trimmedName = (formValues.Pack_Name || "").trim();
    const parsed = parseCurrency(formValues.Pack_Price);
    const priceValue =
      typeof parsed === "number" && !Number.isNaN(parsed)
        ? Number(parsed)
        : Number.parseFloat(String(formValues.Pack_Price || "0").replace(/,/g, ""));

    if (!trimmedName) {
      getAlert("WARNING", "กรุณาระบุชื่อแพ็คเก็จ");
      return false;
    }

    if (
      formValues.Pack_Price !== "" &&
      (Number.isNaN(priceValue) || priceValue < 0)
    ) {
      getAlert("WARNING", "กรุณากรอกราคาแพ็คเก็จให้ถูกต้อง");
      return false;
    }

    if (selectedServices.length === 0) {
      getAlert("WARNING", "กรุณาเลือกบริการอย่างน้อย 1 รายการเพื่อใช้ในแพ็คเก็จ");
      return false;
    }

    return true;
  };

  const getPayload = () => {
    const trimmedName = (formValues.Pack_Name || "").trim();
    const parsed = parseCurrency(formValues.Pack_Price);
    const priceNumber =
      typeof parsed === "number" && !Number.isNaN(parsed)
        ? Number(parsed)
        : Number.parseFloat(String(formValues.Pack_Price || "0").replace(/,/g, "")) ||
          0;
    const normalizedPrice = Number.isFinite(priceNumber)
      ? Number(priceNumber.toFixed(2))
      : 0;

    return {
      Pack_Name: trimmedName,
      Pack_Price: normalizedPrice,
      Pack_Status: formValues.Pack_Status === "N" ? "N" : "Y",
      ...(companyId ? { Comp_Id: companyId } : {}),
      Pack_Img:
        formValues.Pack_Img && formValues.Pack_Img !== "" ? formValues.Pack_Img : null,
    };
  };

  const savePackageDetails = async (packId, services, shouldReplace) => {
    if (shouldReplace) {
      await deleteData("Tb_Set_Package_D", `WHERE Pack_Id = ${packId}`);
    }

    if (!Array.isArray(services) || services.length === 0) {
      return;
    }

    for (const service of services) {
      const response = await insertData(
        "Tb_Set_Package_D",
        "Pack_Id, Service_Id",
        [packId, service.Service_Id]
      );
      if (response?.status !== "OK") {
        throw new Error(response?.message || "ไม่สามารถบันทึกบริการในแพ็คเก็จได้");
      }
    }
  };

  const insertPackage = async (payload, services) => {
    const { Pack_Name, Pack_Status, Pack_Price, Comp_Id } = payload;
    const fields = ["Pack_Name", "Pack_Status", "Pack_Price"];
    const values = [Pack_Name, Pack_Status, Pack_Price];

    if (Comp_Id) {
      fields.push("Comp_Id");
      values.push(Comp_Id);
    }
    if (Object.prototype.hasOwnProperty.call(payload, "Pack_Img")) {
      fields.push("Pack_Img");
      values.push(payload.Pack_Img);
    }

    const insertResponse = await insertData(
      "Tb_Set_Package_H",
      fields.join(", "),
      values
    );

    if (insertResponse?.status !== "OK") {
      throw new Error(insertResponse?.message || "ไม่สามารถบันทึกข้อมูลได้");
    }

    let packId =
      insertResponse?.insertId ||
      insertResponse?.Pack_Id ||
      insertResponse?.id;

    if (!packId) {
      const refresh = await getData(
        "Tb_Set_Package_H",
        "1=1 ORDER BY Pack_Id DESC"
      );
      if (Array.isArray(refresh) && refresh.length > 0) {
        packId = refresh[0].Pack_Id;
      }
    }

    if (!packId) {
      throw new Error("ไม่สามารถระบุรหัสแพ็คเก็จที่สร้างได้");
    }

    await savePackageDetails(packId, services, false);
  };

  const updatePackage = async (payload, services) => {
    if (!formValues.Pack_Id) {
      throw new Error("ไม่พบรหัสแพ็คเก็จ");
    }

    await updateData("Tb_Set_Package_H", payload, { Pack_Id: formValues.Pack_Id });
    await savePackageDetails(formValues.Pack_Id, services, true);
  };

  const handleSubmit = React.useCallback(async () => {
    if (!validateForm()) return;

    if (isSavingRef.current) return;

    const payload = getPayload();
    const services = selectedServices.map((service) => ({
      Service_Id: service.Service_Id,
    }));

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await insertPackage(payload, services);
      } else {
        await updatePackage(payload, services);
      }
      await callInitialize();
      if (typeof setMode === "function") {
        setMode("S");
      }
    } catch (error) {
      getAlert("FAILED", error.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [mode, selectedServices, formValues, callInitialize, setMode]);

  const handleDelete = () => {
    if (!formValues.Pack_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูลแพ็คเก็จนี้หรือไม่?", async (isConfirm) => {
      if (!isConfirm) return;
      try {
        await deleteData("Tb_Set_Package_D", `WHERE Pack_Id = ${formValues.Pack_Id}`);
        await deleteData("Tb_Set_Package_H", `WHERE Pack_Id = ${formValues.Pack_Id}`);
        await callInitialize();
        if (typeof setMode === "function") {
          setMode("S");
        }
      } catch (error) {
        getAlert("FAILED", error.message || "ไม่สามารถลบข้อมูลได้");
      }
    });
  };

  const filteredServices = useMemo(() => {
    const keyword = serviceSearch.trim().toLowerCase();
    if (!keyword) return serviceOptions;
    return serviceOptions.filter((service) => {
      const values = [service.Service_Code, service.Service_Name];
      return values.some(
        (value) =>
          typeof value === "string" && value.toLowerCase().includes(keyword)
      );
    });
  }, [serviceOptions, serviceSearch]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        handleBack();
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  return (
    <>
      <InitialDataFormLayout
        name={name}
        entityPath="/package"
        mode={mode}
        onSubmit={handleSubmit}
        onBack={handleBack}
        onReset={handleReset}
        onDelete={mode === "U" ? handleDelete : undefined}
        isSubmittingDisabled={isSaving}
        submitLabelInsert="บันทึก"
        submitLabelUpdate={isSaving ? "กำลังบันทึก..." : "บันทึก"}
        callInitialize={callInitialize}
      >
        <div style={sectionHeaderStyle}>
          <i className="fas fa-box-open" style={sectionIconStyle}></i>
          <h5 style={sectionTitleStyle}>รายละเอียดแพ็คเก็จ</h5>
        </div>
        <div className="row g-4">
          <div className="col-lg-6">
            <label style={labelStyle}>
              ชื่อแพ็คเก็จ <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="Pack_Name"
              value={formValues.Pack_Name || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <label style={labelStyle}>ราคาแพ็คเก็จ</label>
            <input
              type="text"
              className="form-control"
              name="Pack_Price"
              value={formValues.Pack_Price || ""}
              onChange={handleChange}
              onFocus={handlePackPriceFocus}
              onBlur={handlePackPriceBlur}
              style={{ ...inputStyle, textAlign: "right" }}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <label style={labelStyle}>สถานะ</label>
            <select
              className="form-select"
              name="Pack_Status"
              value={formValues.Pack_Status || "Y"}
              onChange={handleChange}
              style={selectStyle}
            >
              <option value="Y">ใช้งาน</option>
              <option value="N">ไม่ใช้งาน</option>
            </select>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-4">
            <label style={labelStyle}>รูปแพ็คเก็จ</label>
            <div className="d-flex flex-column flex-md-row align-items-start gap-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={previewContainerStyle}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="แพ็คเก็จ" style={previewImageStyle} />
                ) : (
                  <span className="text-muted">ไม่มีรูปภาพ</span>
                )}
              </div>
              <div className="d-flex flex-column gap-2">
                <button
                  type="button"
                  className="btn"
                  style={uploadButtonStyle}
                  onClick={triggerFileDialog}
                  disabled={isSaving}
                >
                  อัปโหลด
                </button>
                {imagePreview && (
                  <button
                    type="button"
                    className="btn"
                    style={removeButtonStyle}
                    onClick={handleRemoveImage}
                    disabled={isSaving}
                  >
                    ลบรูป
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <div style={sectionHeaderStyle}>
            <i className="fas fa-list" style={sectionIconStyle}></i>
            <h5 style={sectionTitleStyle}>บริการในแพ็คเก็จ</h5>
          </div>
          <button
            type="button"
            className="btn btn-warning text-white"
            onClick={() => setIsServiceModalOpen(true)}
            disabled={isSaving}
          >
            เลือกบริการ
          </button>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <div
              className="table-responsive"
              style={{ maxHeight: "350px", overflowY: "auto" }}
            >
              <table className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: "10%" }}>#</th>
                    <th className="text-center" style={{ width: "20%" }}>รหัสบริการ</th>
                    <th className="text-center" style={{ width: "60%" }}>ชื่อบริการ</th>
                    <th className="text-center" style={{ width: "10%" }}>ลบ</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedServices.length > 0 ? (
                    selectedServices.map((service, index) => (
                      <tr key={service.Service_Id || index}>
                        <td className="text-center">{index + 1}</td>
                        <td className="text-left">{service.Service_Code || "-"}</td>
                        <td className="text-left">{service.Service_Name || "-"}</td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveService(service.Service_Id)}
                            disabled={isSaving}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        ยังไม่มีบริการในแพ็คเก็จ
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </InitialDataFormLayout>

      {isServiceModalOpen && (
        <div
          className="modal"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">เลือกบริการ</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setIsServiceModalOpen(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ค้นหาบริการ..."
                    value={serviceSearch}
                    onChange={(event) => setServiceSearch(event.target.value)}
                  />
                </div>
                <div
                  className="table-responsive"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                      <tr>
                        <th className="text-center" style={{ width: "10%" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              modalSelection.length > 0 &&
                              modalSelection.length === selectedServices.length
                            }
                            onChange={() => {
                              if (modalSelection.length === selectedServices.length) {
                                setModalSelection([]);
                              } else {
                                setModalSelection(
                                  selectedServices.map((service) =>
                                    Number(service.Service_Id)
                                  )
                                );
                              }
                            }}
                          />
                        </th>
                        <th className="text-center" style={{ width: "20%" }}>
                          รหัสบริการ
                        </th>
                        <th className="text-left" style={{ width: "70%" }}>
                          ชื่อบริการ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredServices.length > 0 ? (
                        filteredServices.map((service) => {
                          const normalizedId = Number(service.Service_Id);
                          const checked = modalSelection.includes(normalizedId);
                          return (
                            <tr
                              key={service.Service_Id}
                              className={checked ? "table-active" : ""}
                              onClick={() => toggleModalSelection(normalizedId)}
                              style={{ cursor: "pointer" }}
                            >
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  onChange={() => toggleModalSelection(normalizedId)}
                                />
                              </td>
                              <td className="text-center">{service.Service_Code}</td>
                              <td className="text-left">{service.Service_Name}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center py-4 text-muted">
                            ไม่พบบริการที่ค้นหา
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsServiceModalOpen(false)}
                >
                  ปิด
                </button>
                <button
                  type="button"
                  className="btn btn-info text-white"
                  onClick={handleServiceModalConfirm}
                >
                  ยืนยันการเลือก
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isServiceModalOpen && <div className="modal-backdrop fade show" />}
    </>
  );
};

export default PackageForm;
